import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { useUploadProjectMutation } from "@/redux/slices/offline/offlineApiSlice";
import { showSuccessToast, showErrorToast } from "@/components/toast/toast";
import { OfflineProjectService } from "@/services/OfflineProjectService";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useDownloadProjectMutation } from "@/redux/slices/offline/offlineApiSlice";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

interface UploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  project: any;
}

const UPLOAD_SECTIONS = [
  { id: 'define', label: 'Define', disabled: true },
  { id: 'palletConfig', label: 'Pallet Configuration', disabled: true },
  { id: 'lpnNumber', label: 'LPN Number', disabled: true },
  { id: 'preShip', label: 'Pre Ship' },
  { id: 'truckLayout', label: 'Truck Layout' },
  { id: 'postShip', label: 'Post Ship' },
  { id: 'stackTestLayout', label: 'Stack Test Layout' },
  { id: 'postStack', label: 'Post Stack' },
  { id: 'synopsis', label: 'Synopsis' }
];

export function UploadModal({ isOpen, onClose, project }: UploadModalProps) {
  const [selectedSections, setSelectedSections] = useState<string[]>([]);
  const [uploadProject, { isLoading }] = useUploadProjectMutation();
  const [deleteAfterUpload, setDeleteAfterUpload] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [downloadProject] = useDownloadProjectMutation();

  const handleSectionToggle = (sectionId: string) => {
    setSelectedSections(prev =>
      prev.includes(sectionId)
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const handleUploadClick = () => {
    if (selectedSections.length === 0) {
      showErrorToast("Please select at least one section to upload");
      return;
    }

    if (!deleteAfterUpload) {
      setShowConfirmDialog(true);
    } else {
      handleUpload();
    }
  };

  const handleUpload = async () => {
    try {
      const projectData = await OfflineProjectService.getProjectDataForUpload(project.id, selectedSections);
      const response = await uploadProject({
        projectId: project.id,
        onlineId: project.offlineMetadata?.onlineId,
        selectedSections,
        projectData: projectData
      }).unwrap();

      if (deleteAfterUpload) {
        await OfflineProjectService.deleteProject(project.id);
        showSuccessToast("Project uploaded and deleted from offline storage");
        window.location.reload();
      } else {
        const newRes = await downloadProject(project.id).unwrap();
        await OfflineProjectService.saveProjectOffline(newRes.data);
        showSuccessToast("Project uploaded successfully");
      }

      setShowConfirmDialog(false);
      onClose();
    } catch (error) {
      console.error('Error uploading project:', error);
      showErrorToast("Failed to upload project");
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50 transition-opacity" onClick={onClose} />
      <div className="fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 sm:rounded-lg">
        <div className="flex flex-col space-y-1.5 text-center sm:text-left">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold leading-none tracking-tight">Upload Project Sections</h2>
            <Button
              variant="ghost"
              className="h-8 w-8 p-0"
              onClick={onClose}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">
            Select the sections you want to upload to the online database.
          </p>
        </div>

        <div className="grid gap-4 py-4">
          {UPLOAD_SECTIONS.map((section) => (
            <div key={section.id} className="flex items-center space-x-2">
              <Checkbox
                id={section.id}
                checked={selectedSections.includes(section.id)}
                onCheckedChange={() => handleSectionToggle(section.id)}
                disabled={section.disabled}
                className={section.disabled ? "opacity-50 cursor-not-allowed" : ""}
              />
              <Label
                htmlFor={section.id}
                className={`text-sm font-medium leading-none peer-disabled:cursor-not-allowed ${section.disabled ? "opacity-50" : ""}`}
              >
                {section.label}
              </Label>
            </div>
          ))}

          <div className="flex items-center space-x-2 pt-4">
            <Switch
              id="delete-after-upload"
              checked={deleteAfterUpload}
              onCheckedChange={setDeleteAfterUpload}
            />
            <Label htmlFor="delete-after-upload">Delete from offline storage after uploading</Label>
          </div>
        </div>

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleUploadClick} disabled={isLoading}>
            {isLoading ? 'Uploading...' : 'Upload Selected Sections'}
          </Button>
        </div>
      </div>

      {showConfirmDialog && (
        <>
          <div className="fixed inset-0 bg-black/50 z-[60] transition-opacity" onClick={() => setShowConfirmDialog(false)} />
          <div className="fixed left-[50%] top-[50%] z-[60] grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 sm:rounded-lg">
            <div className="flex flex-col space-y-1.5 text-center sm:text-left">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold leading-none tracking-tight">Confirm Upload</h2>
                <Button
                  variant="ghost"
                  className="h-8 w-8 p-0"
                  onClick={() => setShowConfirmDialog(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-sm text-muted-foreground">
                Are you sure you want to proceed? All selected sections in the local database will be replaced with online data.
              </p>
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
              <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpload} disabled={isLoading}>
                Proceed with Upload {isLoading ? '...' : ''}
              </Button>
            </div>
          </div>
        </>
      )}
    </>
  );
}
